import React from "react";

import { Box, Typography, useMediaQuery } from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import CircularProgressBar from "@/components/AllergenModal/CircularProgressBar";
import CalorieFireIcon from "@/components/Icons/CalorieFireIcon";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import { percent, round } from "@/utils/number";

import { useRecipe } from "../store/RecipeStore";

const useStyles = makeStyles()(() => ({
  progressContainer: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 100,
    display: "flex",
    gap: 20,
    padding: "12px 24px 12px 12px",
  },
  caloriesContainer: {
    backgroundColor: colors.white,
    padding: "32px",
  },
  bodyText4: {
    fontWeight: 600,
    fontSize: 16,
    color: "#595F68",
  },
}));

const Macronutrients: React.FC = () => {
  const { classes, cx } = useStyles();
  const recipe = useRecipe();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    calories,
    carbohydrates,
    proteins,
    fats,
    totalCarbsGrams,
    totalProteinGrams,
    totalFatGrams,
  } = recipe.totalMacros;

  const renderMobileMacro = (
    letter: string,
    grams: number,
    ratio: number,
    color: string
  ) => (
    <Box display="flex" flexDirection="column" alignItems="center" minWidth={0}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Box
          width={8}
          height={8}
          borderRadius="50%"
          bgcolor={color}
          flexShrink={0}
        />
        <Typography fontWeight={700} fontSize={13} color={colors.dark}>
          {letter}
        </Typography>
      </Box>
      <Typography fontWeight={700} fontSize={13} color={colors.dark}>
        {grams}g
      </Typography>
      <Typography fontSize={11} color={colors.gray}>
        {percent(ratio, { round: 0 })}%
      </Typography>
    </Box>
  );

  if (isMobile) {
    return (
      <Box>
        <Typography fontSize={20} fontWeight={700} variant="body1">
          Macronutrients
        </Typography>
        <Box height={6} />
        <Box
          alignItems="center"
          bgcolor={colors.light}
          display="flex"
          justifyContent="space-between"
          gap={1}
          padding={2}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box fontSize="26px" display="flex">
              <CalorieFireIcon fontSize="inherit" />
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography fontWeight={700} fontSize={15} color={colors.dark}>
                {round(calories, 2)}
              </Typography>
              <Typography fontSize={11} color="#595F68">
                kcal
              </Typography>
            </Box>
          </Box>
          {renderMobileMacro(
            "C",
            totalCarbsGrams,
            carbohydrates,
            colors.primary
          )}
          {renderMobileMacro(
            "P",
            totalProteinGrams,
            proteins,
            colors.secondary
          )}
          {renderMobileMacro("F", totalFatGrams, fats, "#2D87BB")}
        </Box>
        <Box margin={0.5} />
        <Typography fontSize={14}>
          Note: The calories and macronutrients are automatically generated
          based on the ingredients added.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography fontSize={20} fontWeight={700} variant="body1">
        Macronutrients
      </Typography>
      <Box height={6} />
      <Box
        alignItems="center"
        bgcolor={colors.light}
        display="flex"
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        gap={{ xs: 2, md: 5 }}
        padding={2}
      >
        <Box
          className={cx(classes.progressContainer, classes.caloriesContainer)}
        >
          <Box fontSize="48px">
            <CalorieFireIcon fontSize="inherit" />
          </Box>
          <Box>
            <Typography
              textAlign="center"
              fontWeight={700}
              fontSize={28}
              color={colors.dark}
            >
              {round(calories, 4)}
            </Typography>
            <Typography
              textAlign="center"
              fontWeight={600}
              fontSize={14}
              color="#595F68"
            >
              Calories
            </Typography>
          </Box>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(carbohydrates, { round: 0 })}%`}
            label={`${totalCarbsGrams}g`}
            data={[
              {
                value: percent(carbohydrates),
                color: colors.primary,
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Carbs</Typography>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(proteins, {
              round: 0,
            })}%`}
            label={`${totalProteinGrams}g`}
            data={[
              {
                value: percent(proteins),
                color: colors.secondary,
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Protein</Typography>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(fats, { round: 0 })}%`}
            label={`${totalFatGrams}g`}
            data={[
              {
                value: percent(fats),
                color: "#2D87BB",
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Fats</Typography>
        </Box>
      </Box>
      <Box margin={0.5} />
      <Typography fontSize={14}>
        Note: The calories and macronutrients are automatically generated based
        on the ingredients added.
      </Typography>
    </Box>
  );
};

export default observer(Macronutrients);
